import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import {
  Star as StarIcon,
  Group as GroupIcon,
  Favorite,
  FavoriteBorder
} from '@mui/icons-material';
import { Event, SportType } from '../types/event';
import SportsIcon from './SportsIcon';
import { useAuth } from '../contexts/AuthContext';
import { getUserInitials } from '../utils/userUtils';
import { getLevelDisplayName, getGenericLevelName } from '../utils/levelDefinitions';

interface EventCardProps {
  event: Event;
  onClick?: () => void;
  extraBadge?: React.ReactNode;
  variant?: 'vertical' | 'horizontal';
  isHost?: boolean;
  showStatus?: boolean;
  userStatus?: string;
  onLikeToggle?: (eventId: string, isCurrentlyLiked: boolean) => void;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onClick,
  extraBadge,
  variant = 'vertical',
  isHost = false,
  showStatus = false,
  userStatus,
  onLikeToggle
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleHostClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event card click
    if (event.created_by) {
      // If clicking on own profile, go to /profile instead of /user/{id}
      if (user && event.created_by === user.id) {
        navigate('/profile');
      } else {
        navigate(`/user/${event.created_by}`);
      }
    }
  };

  const getSportGradient = (sportType: string): string => {
    return '#F7F7F7';
  };

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return {
      dayName,
      monthDay,
      year,
      time,
      fullDate: `${dayName}, ${monthDay}, ${year}`,
      timeRange: `${time} - ${new Date(date.getTime() + 2 * 60 * 60 * 1000).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`
    };
  };



  const { fullDate, timeRange } = formatDateTime(event.time);

  if (variant === 'horizontal') {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 },
          width: '100%',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.2s ease',
          '@media (hover: hover) and (pointer: fine)': {
            '&:hover': onClick ? {
              transform: 'translateY(-2px)'
            } : {}
          },
          '@media (hover: none) and (pointer: coarse)': {
            '&:active': onClick ? {
              transform: 'scale(0.98)'
            } : {}
          }
        }}
      >
        {/* Image Section */}
        <Box
          sx={{
            width: { xs: '100%', sm: 200 },
            height: { xs: 180, sm: 120 },
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0
          }}
        >
          {/* Enhanced Gradient Placeholder (Option E) - Always render as background */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: '#F7F7F7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 0,
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: '#E8DDFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <SportsIcon sport={event.sport_type as SportType} size={30} style={{ color: '#533B7D' }} />
            </Box>
          </Box>

          {/* Main Image - Overlay on top if exists and valid */}
          {event.images?.image_1 && typeof event.images.image_1 === 'string' && event.images.image_1.trim() !== '' && (
            <img
              src={event.images.image_1}
              alt={event.title}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 1
              }}
              onError={(e) => {
                // If image fails to load, hide it to reveal the placeholder
                e.currentTarget.style.display = 'none';
              }}
            />
          )}

          {/* Sport Tag */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              px: 1.5,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
            }}
          >
            <SportsIcon sport={event.sport_type as SportType} size={12} style={{ color: '#533B7D' }} />
            <Typography variant="caption" sx={{ color: '#333333', fontWeight: 600 }}>
              {event.sport_type}
            </Typography>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', py: 0.5 }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                {event.title}
              </Typography>

              {/* Status Badge */}
              {(showStatus || isHost) && (
                <Chip
                  label={isHost ? 'Hosting' : userStatus || 'Joined'}
                  size="small"
                  sx={{
                    backgroundColor: isHost ? '#E8DDFF' : userStatus === 'ACCEPTED' ? '#E8F5E9' : '#FFF3E0',
                    color: isHost ? '#533B7D' : userStatus === 'ACCEPTED' ? '#2E7D32' : '#E65100',
                    fontWeight: 'bold',
                    borderRadius: '16px'
                  }}
                />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {fullDate} • {timeRange}
            </Typography>

            <Typography variant="body2" color="primary" fontWeight="medium">
              {event.address || event.formatted_address || event.location}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Tags */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {event.tags?.slice(0, 3).map(tag => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '10px' }}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <StarIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">
                {event.level_requirement && event.activity_type
                  ? getLevelDisplayName(event.level_requirement, event.activity_type)
                  : event.level_needed || 'Any Level'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <GroupIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">{event.capacity - event.occupied_spots}/{event.capacity} spots open</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: 296 },
        cursor: onClick ? 'pointer' : 'default',
        // Desktop: hover lift effect
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            transform: 'translateY(-4px)',
            transition: 'transform 0.3s ease',
            '& .event-card-image': {
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
            }
          }
        },
        // Mobile: tap feedback
        '@media (hover: none) and (pointer: coarse)': {
          '&:active': {
            transform: 'scale(0.98)',
            transition: 'transform 0.1s ease'
          }
        }
      }}
    >
      {/* Image Container */}
      <Box
        className="event-card-image"
        sx={{
          height: { xs: 180, sm: 200, md: 206 },
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          mb: 1.5,
          transition: 'box-shadow 0.3s ease'
        }}
      >
        {/* Enhanced Gradient Placeholder (Option E) - Always render as background */}
        <Box
          className="default-bg"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#F7F7F7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: '#E8DDFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <SportsIcon
              sport={event.sport_type as SportType}
              size={50}
              style={{
                color: '#533B7D',
              }}
            />
          </Box>
        </Box>

        {/* Main Image - Overlay on top if exists and valid */}
        {event.images?.image_1 && typeof event.images.image_1 === 'string' && event.images.image_1.trim() !== '' && (
          <img
            src={event.images.image_1}
            alt={event.title}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 1
            }}
            onError={(e) => {
              // If image fails to load, hide it to reveal the placeholder
              e.currentTarget.style.display = 'none';
            }}
          />
        )}

        {/* Sport Tag */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}
        >
          <SportsIcon
            sport={event.sport_type as SportType}
            size={16}
            style={{ color: '#533B7D' }}
          />
          <span
            style={{
              color: '#333333',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '16px'
            }}
          >
            {event.sport_type}
          </span>
        </div>

        {/* Free to Join Badge */}
        {event.auto_accept && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              zIndex: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
            }}
          >
            <span
              style={{
                color: '#4caf50',
                fontSize: '11px',
                fontWeight: 700,
                lineHeight: '14px',
                letterSpacing: '0.3px',
                textTransform: 'uppercase'
              }}
            >
              Free to Join
            </span>
          </div>
        )}

        {/* Status Badge (Hosting/Joined/Pending) */}
        {(showStatus || isHost) && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
            <Chip
              label={isHost ? 'Hosting' : userStatus || 'Joined'}
              size="small"
              sx={{
                backgroundColor: isHost ? '#E8DDFF' : userStatus === 'ACCEPTED' ? '#E8F5E9' : '#FFF3E0',
                color: isHost ? '#533B7D' : userStatus === 'ACCEPTED' ? '#2E7D32' : '#E65100',
                fontWeight: 'bold',
                borderRadius: '16px',
                height: 24,
                boxShadow: '0 2px 4px rgba(0,0,0,0.12)'
              }}
            />
          </Box>
        )}

        {/* Extra Badge (e.g. Pending, Expired) - Only show if no status badge to avoid overlap, or stack them */}
        {extraBadge && !showStatus && !isHost && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
            {extraBadge}
          </Box>
        )}

        {/* User Avatar */}
        <Box
          onClick={handleHostClick}
          sx={{
            position: 'absolute',
            top: 182,
            left: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            zIndex: 11,
            '&:hover': {
              '& .host-name': {
                textDecoration: 'underline'
              }
            }
          }}
        >
          <Avatar
            src={event.host_profile_picture || undefined}
            sx={{
              width: 16,
              height: 16,
              backgroundColor: event.host_profile_picture ? undefined : '#eaddff',
              fontSize: '8px',
              fontWeight: 'bold',
              color: '#533b7d'
            }}
          >
            {!event.host_profile_picture && getUserInitials(event.host_name || 'Host')}
          </Avatar>
          <Typography
            className="host-name"
            variant="body2"
            sx={{
              color: 'white',
              fontSize: '11px',
              fontWeight: 'bold'
            }}
          >
            {event.host_name || 'Host'}
          </Typography>
        </Box>

        {/* Shadow Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 40,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
            pointerEvents: 'none'
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            zIndex: 10
          }}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              onLikeToggle?.(event.id, event.is_liked_by_user || false);
            }}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              minWidth: 32,
              height: 32,
              borderRadius: 16,
              padding: (event.like_count || 0) > 0 ? '0 8px' : '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
            }}
          >
            {event.is_liked_by_user ? (
              <Favorite sx={{ fontSize: 18, color: '#ff4081' }} />
            ) : (
              <FavoriteBorder sx={{ fontSize: 18, color: '#666' }} />
            )}
            {(event.like_count || 0) > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: '#333',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  lineHeight: 1
                }}
              >
                {event.like_count}
              </Typography>
            )}
          </div>
        </Box>
      </Box>

      {/* Event Details */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            fontSize: '16px',
            lineHeight: '24px',
            color: '#000',
            mb: 0.5
          }}
        >
          {event.title}
        </Typography>

        {/* Date and Time */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: '#000'
            }}
          >
            {fullDate}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: '#000'
            }}
          >
            {timeRange}
          </Typography>
        </Box>

        {/* Location */}
        <Typography
          variant="body2"
          sx={{
            fontSize: '12px',
            lineHeight: '16px',
            color: '#533b7d',
            fontWeight: 500,
            mb: 1
          }}
        >
          {event.address || event.formatted_address || event.location}
        </Typography>

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 1 }}>
            {event.tags.slice(0, 3).map((tag, index) => {
              // Randomly determine if this tag should twinkle
              const shouldTwinkle = Math.random() > 0.5;
              const animationDelay = Math.random() * 2; // Random delay 0-2s

              return (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '11px',
                    fontWeight: 500,
                    backgroundColor: 'transparent',
                    border: '1px solid #e3e3e3',
                    color: '#000',
                    '& .MuiChip-label': {
                      px: 0.75,
                      py: 0.375
                    },
                    // Desktop: hover effect
                    '@media (hover: hover) and (pointer: fine)': {
                      '&:hover': {
                        backgroundColor: 'rgba(83, 59, 125, 0.08)',
                        borderColor: '#533b7d',
                        transform: 'scale(1.05)',
                        transition: 'all 0.2s ease'
                      }
                    },
                    // Mobile: twinkling animation
                    '@media (hover: none) and (pointer: coarse)': {
                      animation: shouldTwinkle ? `twinkle 3s ease-in-out ${animationDelay}s infinite` : 'none',
                      '@keyframes twinkle': {
                        '0%, 100%': {
                          backgroundColor: 'transparent',
                          borderColor: '#e3e3e3',
                          boxShadow: 'none'
                        },
                        '50%': {
                          backgroundColor: 'rgba(83, 59, 125, 0.12)',
                          borderColor: '#533b7d',
                          boxShadow: '0 0 8px rgba(83, 59, 125, 0.3)'
                        }
                      }
                    }
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Level and Spots */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StarIcon sx={{ fontSize: 16, color: '#6a6a6a' }} />
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                lineHeight: '16px',
                color: '#6a6a6a'
              }}
            >
              {event.level_requirement && event.activity_type
                ? getLevelDisplayName(event.level_requirement, event.activity_type)
                : event.level_needed || 'Any Level'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GroupIcon sx={{ fontSize: 16, color: '#6a6a6a' }} />
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                lineHeight: '16px',
                color: '#6a6a6a'
              }}
            >
              {event.capacity - event.occupied_spots}/{event.capacity} spots open
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EventCard;
